import { CommonDialogs } from "@mcleod/common";
import { ChangeEvent, ClickEvent, DataSourceExecutionEvent, MapPin, Switch, TableAddRowResult, TableContentsChangedEvent, TableRow, TableSelectionEvent, Textbox } from "@mcleod/components";
import { NoMap } from "@mcleod/components/src/components/map/NoMap";
import { TrimbleMap } from "@mcleod/components/src/components/map/TrimbleMap";
import { MapSettings, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutMobileCommLandmarkAndMapView } from "./autogen/AutogenLayoutMobileCommLandmarkAndMapView";

export class MobileCommLandmarkAndMapView extends AutogenLayoutMobileCommLandmarkAndMapView {
    private _pinMap = {};
    private mapLandmark;
    private _ignoreSelectionListener = false;

    private _dragEndListener = ((event) => {
        const pinId = event.pin.pinId;
        const lat = event.pin.endLatitude;
        const lng = event.pin.endLongitude;
        const fixedLat = Number(lat) || null;
        const fixedLng = Number(lng) || null;
        let selectedRow = this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRow;

        if (!selectedRow) {
            const pin = new MapPin(fixedLat, fixedLng);
            pin.layoutName = null;
            pin.pinId = pinId;
            this.mapPinOnClick?.(null, pin, false);
            selectedRow = this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRow;
        }

        if (selectedRow) {
            CommonDialogs.showYesNo("Update landmark (" + pinId + ") with dropped pin latitude longitude?", "Update Mobile Comm Landmark").then(result => {
                if (result === undefined) return;
                if (result) {
                    const data: ModelRow = selectedRow.data;
                    data.set("latitude", fixedLat.toFixed(6));
                    data.set("longitude", Math.abs(fixedLng).toFixed(6));
                    selectedRow.data.set("latitude", fixedLat.toFixed(6));
                    selectedRow.data.set("longitude", Math.abs(fixedLng).toFixed(6));
                    data.post();
                } else {
                    this.mapAllLandmarks(false);
                }
            });
        } else {
            const fixedLat = Number(lat) || null;
            const fixedLng = Number(lng) || null;
            if (fixedLat && fixedLng) {
                this.layoutMobileCommLandmarkTools.textboxLatitude.text = fixedLat.toFixed(6);
                this.layoutMobileCommLandmarkTools.textboxLongitude.text = fixedLng.toFixed(6);
            }
        }
    });


    override onLoad(): void {
        this.addLayoutLoadListener(() => {

            this.setupMobileCommLandmarkListLayout();
            this.setupMobileCommLandmarkToolsLayout();

            this.googleMap.addDragEndListener(this._dragEndListener);
            this.trimbleMap.addDragEndListener(this._dragEndListener);
            this.googleMap.createMapCallback = () => {
                this.layoutMobileCommLandmarkTools.buttonAddLandmark.enabled = true;
                this.mapAllLandmarks();
            }
        });
    }

    private setNewPinLocation(latitude: number, longitude: number): ModelRow {
        this.mapLandmark.removeAllPins();

        const modelRow = new ModelRow("lme/dispatch/mobilecomm/mobile-comm-landmark", false, { id: "temp", latitude: latitude, longitude: longitude });
        this.addPinAtLocation(modelRow, false);
        this.mapLandmark.setCenter(latitude, Math.abs(longitude));
        this.layoutMobileCommLandmarkTools.textboxLatitude.text = latitude.toFixed(6);
        this.layoutMobileCommLandmarkTools.textboxLongitude.text = longitude.toFixed(6);
        return modelRow;
    }

    private mapAllLandmarks(resetZoom: boolean = true) {
        if (this.mapLandmark.map instanceof NoMap) {
            return;
        }

        this.mapLandmark.removeAllPins();
        this.layoutMobileCommLandmarkList.tableMobileCommLandmark.filteredRows
            .filter(row => row.data.get("is_active") == "Y")
            .forEach(tableRow => {
                const modelRow = tableRow.data as ModelRow;
                this.addPinAtLocation(modelRow, false);
            });

        if (resetZoom) {
            this.mapLandmark.fitPins();
            this.setZoomLevel();
        }
    }

    private onSelectionChanged(event: TableSelectionEvent) {
        if (this._ignoreSelectionListener || this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRows.length === 0) {
            return;
        }

        const modelRow = event.newRow?.data as ModelRow;

        if (this.mapLandmark.map instanceof NoMap) {
            return;
        }

        if (modelRow != null) {
            this.addPinAtLocation(modelRow, true);
        } else {
            this.mapAllLandmarks();
        }
    }

    private setZoomLevel() {
        // I don't like doing this, but Trimble doesn't seem to play nicely with the fitPins call
        if (this.mapLandmark.map instanceof TrimbleMap) {
            this.mapLandmark.setZoom(3);
        }
    }

    private addPinAtLocation(modelRow: ModelRow, focusOnPin: boolean) {
        if (modelRow.get("is_active") === "N") {
            return;
        }
        const lat = modelRow.get("latitude");
        const lon = modelRow.get("longitude");

        if (lat && lon) {
            const pin = new MapPin(lat, lon);
            pin.layoutName = null;
            pin.pinId = modelRow.get("id");
            pin.image = "map-pin-location";
            pin.tooltip = "Location: " + modelRow.get("id");
            pin.draggable = true;
            pin.onClick = (e: ClickEvent) => {
                this.mapPinOnClick?.(e, pin);
            }

            if (focusOnPin) {
                if (!this._pinMap.hasOwnProperty(pin.pinId)) {
                    this._pinMap[pin.pinId] = pin;
                }
                this.mapLandmark.setCenter(lat, Math.abs(lon));
                this.mapLandmark.setZoom(15);
            } else {
                this.mapLandmark.addPin(pin);
                this._pinMap[pin.pinId] = pin;
            }
        }
    }

    private mapPinOnClick(e: ClickEvent, pin: MapPin, setZoom: boolean = true) {
        this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRows = [];
        if (this._pinMap.hasOwnProperty(pin.pinId)) {
            this.layoutMobileCommLandmarkList.tableMobileCommLandmark.rows.forEach(tableRow => {
                const tableModelRow = tableRow.data as ModelRow;
                if (pin.pinId == tableModelRow.get("id")) {
                    this._ignoreSelectionListener = true;
                    this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRow = tableRow;
                    this._ignoreSelectionListener = false;
                    tableRow.scrollIntoView();
                    this.mapLandmark.setCenter(pin.latitude, Math.abs(pin.longitude));
                    if (setZoom) {
                        this.mapLandmark.setZoom(15);
                    }
                    return;
                }
            });

        }
    }

    public async addNewLandmarkToTable(): Promise<any> {
        const appendingModelRow = new ModelRow("lme/dispatch/mobilecomm/mobile-comm-landmark", false);
        const lat = Number(this.layoutMobileCommLandmarkTools.textboxLatitude.text).toFixed(6);
        const lng = Math.abs(Number(this.layoutMobileCommLandmarkTools.textboxLongitude.text)).toFixed(6);

        appendingModelRow.set("id", this.layoutMobileCommLandmarkTools.textboxName.text);
        appendingModelRow.set("descr", this.layoutMobileCommLandmarkTools.textboxDescr.text);
        appendingModelRow.set("is_active", "Y");
        appendingModelRow.set("latitude", lat);
        appendingModelRow.set("longitude", lng);
        const result: TableAddRowResult = this.layoutMobileCommLandmarkList.tableMobileCommLandmark.addRow(appendingModelRow, null, { display: true, addToData: true });

        return true;
    }

    private setupMobileCommLandmarkListLayout() {
        this.layoutMobileCommLandmarkList.addLayoutLoadListener(() => {

            this.layoutMobileCommLandmarkList.tableMobileCommLandmark.addRowDisplayListener(event => {
                const target = event.target as TableRow;
                const lat = target.data.get("latitude");
                const lon = target.data.get("longitude");
                this.layoutMobileCommLandmarkList.tableMobileCommLandmark.dataSource.preventChangeNotifications = false;

                // I am sure this isn't optimal - I am doing this so that clicking into these fields does not trigger the tablerow event
                const textboxDescription: Textbox = event.target.findComponentById("labelShortDesc") as Textbox;
                textboxDescription.addClickListener(event => {
                    if (this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRow == null) {
                        target.selected = true;
                        this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRows = [target];
                    }
                });
                const switchActive: Switch = event.target.findComponentById("switchIsActive") as Switch;
                switchActive.addClickListener(event => {
                    if (this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRow == null) {
                        target.selected = true;
                        this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRows = [target];
                    }
                });
                target.addClickListener(event => {
                    if (target == this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRow) {
                        this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRow = null;
                        this.mapAllLandmarks();
                    } else {
                        const pinId = target.data.get("id");
                        const pin = new MapPin(lat, lon);
                        pin.layoutName = null;
                        pin.pinId = pinId;
                        this.mapPinOnClick?.(null, pin, true);
                    }
                });
            });

            this.layoutMobileCommLandmarkList.sourceMobileCommLandmark.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
                this.mapAllLandmarks();
            });

            this.layoutMobileCommLandmarkList.tableMobileCommLandmark.addSelectionListener((event: TableSelectionEvent) => this.onSelectionChanged(event));
            this.layoutMobileCommLandmarkList.tableMobileCommLandmark.addContentsChangedListener((event: TableContentsChangedEvent) => {
                if (event.getAction() === "delete") {
                    this.mapAllLandmarks(false);
                }
            });
        });

        this.layoutMobileCommLandmarkList.tableMobileCommLandmark.addFilterChangedListener(event => {
            this.mapAllLandmarks();
        });
    }

    private setupMobileCommLandmarkToolsLayout() {
        this.layoutMobileCommLandmarkList.addLayoutLoadListener(() => {
            // Get default from user settings and set drop down and map to this default
            const defaultFindNearVendor = MapSettings.get()?.findnear_vendor;
            if (defaultFindNearVendor === "G") {
                this.googleMap.visible = true;
                this.mapLandmark = this.googleMap;
                this.layoutMobileCommLandmarkTools.buttonAddLandmark.enabled = this.googleMap.clickToActivate == null;
            } else if (defaultFindNearVendor === "W") {
                this.trimbleMap.visible = true;
                this.mapLandmark = this.trimbleMap;
                this.layoutMobileCommLandmarkTools.buttonAddLandmark.enabled = true;
            } else {
                this.noMap.visible = true;
                this.mapLandmark = this.noMap;
                this.layoutMobileCommLandmarkTools.buttonAddLandmark.enabled = false;
            }

            this.layoutMobileCommLandmarkTools.setFindnearVendorItem(defaultFindNearVendor);

            this.layoutMobileCommLandmarkTools.textboxLongitude.addBlurListener((event) => {
                const latitude = Number(this.layoutMobileCommLandmarkTools.textboxLatitude.text) || null;
                const longitude = Number(this.layoutMobileCommLandmarkTools.textboxLongitude.text) || null;

                if (!StringUtil.isEmptyString(latitude) && !StringUtil.isEmptyString(longitude)) {
                    this.setNewPinLocation(latitude, longitude);
                }
            });

            this.layoutMobileCommLandmarkTools.textboxLatitude.addBlurListener((event) => {
                const latitude = Number(this.layoutMobileCommLandmarkTools.textboxLatitude.text) || null;
                const longitude = Number(this.layoutMobileCommLandmarkTools.textboxLongitude.text) || null;

                if (!StringUtil.isEmptyString(latitude) && !StringUtil.isEmptyString(longitude)) {
                    this.setNewPinLocation(latitude, longitude);
                }
            });

            this.layoutMobileCommLandmarkTools.buttonAddLandmark.addClickListener((event) => {
                this.layoutMobileCommLandmarkList.tableMobileCommLandmark.selectedRows = [];
                this.layoutMobileCommLandmarkTools.setComponentsVisibility(true);

                const [lat, lng] = this.mapLandmark.getCenter();
                this.setNewPinLocation(lat, lng);
            });

            this.layoutMobileCommLandmarkTools.buttonCancelLandmark.addClickListener((event) => {
                this.layoutMobileCommLandmarkTools.setComponentsVisibility(false);
                this.mapAllLandmarks(false);
            });

            this.layoutMobileCommLandmarkTools.buttonSaveLandmark.addClickListener((event) => {

                this.layoutMobileCommLandmarkTools.validateId()
                    .then((isValid) => {
                        if (isValid) {
                            this.addNewLandmarkToTable().then(() => {
                                this.layoutMobileCommLandmarkTools.setComponentsVisibility(false);
                                this.mapAllLandmarks(false);
                            });
                        }
                    })
            });

            this.mapLandmark.fillHeight = true;

            this.layoutMobileCommLandmarkTools.textboxFindnearVendor.addChangeListener(async (event: ChangeEvent) => {
                if (event.newValue === "Trimble") {
                    this.trimbleMap.visible = true;
                    this.trimbleMap.fillHeight = true;
                    this.noMap.visible = false;
                    this.noMap.fillHeight = false;
                    this.googleMap.visible = false;
                    this.googleMap.fillHeight = false;
                    this.mapLandmark = this.trimbleMap;
                    this.layoutMobileCommLandmarkTools.buttonAddLandmark.enabled = true;
                    this.mapAllLandmarks();
                } else if (event.newValue === "Google") {
                    this.googleMap.visible = true;
                    this.googleMap.fillHeight = true;
                    this.noMap.visible = false;
                    this.noMap.fillHeight = false;
                    this.trimbleMap.visible = false;
                    this.trimbleMap.fillHeight = false;
                    this.mapLandmark = this.googleMap;
                    this.layoutMobileCommLandmarkTools.buttonAddLandmark.enabled = this.googleMap.clickToActivate == null;
                    this.mapAllLandmarks();
                } else {
                    this.noMap.visible = true;
                    this.noMap.fillHeight = true;
                    this.googleMap.visible = false;
                    this.googleMap.fillHeight = false;
                    this.trimbleMap.visible = false;
                    this.trimbleMap.fillHeight = false;
                    this.mapLandmark = this.noMap;
                    this.layoutMobileCommLandmarkTools.buttonAddLandmark.enabled = false;
                }
            });
        });
    }
}
